<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { getDurationDate } from '@/shared/composables/useEntry'
  import { formatDate as formatDateGlobal, setFormatDate } from '@/shared/utils'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { useCoreStore, useVisitorsStore } from '@/shared/store'
  import { usePerformanceTiming } from '@/shared/composables/usePerformanceTiming'
  import { DURATION_CODE, DURATION_KEYS } from '@/shared/constants/other.constants'
  import { useI18n } from 'vue-i18n'
  import { useValidators } from '@/shared/composables/useValidators'
  import { useValidation } from '@qicgroupdoha/qic-use'

  import { FlowCard } from '@/shared/components/rebranding/FlowCard'
  import CountrySelectQC from '@qicgroupdoha/qic-connect-ds/components/CountrySelectQC'
  import InputDateQC from '@qicgroupdoha/qic-connect-ds/components/InputDateQC'
  import SelectQC from '@qicgroupdoha/qic-connect-ds/components/SelectQC'
  import { PreapprovalForm } from './TravelDateWidget.types'
  import { storeToRefs } from 'pinia'
  import { CountryWithDetailsType } from '@/shared/types/other.types'
  import { useOCRAvailability } from '@/shared/composables/useOCRAvailability'
  import { useFindCountries } from '@/shared/composables/useFindCountries'

  const emit = defineEmits<Emits>()

  interface Emits {
    (e: 'load', request: () => Promise<void>, requestId?: string): void
    (e: 'proceed'): void
  }

  const minDate = new Date()

  const { t, locale } = useI18n()
  const { onPerformanceCheckEnd } = usePerformanceTiming()
  const coreStore = useCoreStore()
  const visitorsStore = useVisitorsStore()
  const gaService = useGoogleAnalytics()
  const { createRequiredValidator, required } = useValidators()
  const { findCountries } = useFindCountries()
  const { isOcrAvailable } = useOCRAvailability()

  const { nationalities } = storeToRefs(coreStore)
  const localNationalities = computed<CountryWithDetailsType[]>(() =>
    nationalities.value?.map((nat) => ({
      name: nat.desc,
      iso: nat.flag,
      ...nat,
    }))
  )
  const nationality = ref<CountryWithDetailsType | null>(null)
  const holderNationality = () => {
    const nationality =
      localNationalities.value.find((nationality) => {
        if (visitorsStore.lastHolderNationality !== visitorsStore.holder.nationality) {
          return nationality.code === visitorsStore.lastHolderNationality
        }
        return nationality.code === visitorsStore.holder.nationality
      }) || null
    return nationality
  }

  watch(
    localNationalities,
    () => {
      nationality.value = holderNationality()
    },
    {
      immediate: true,
    }
  )

  const preapprovalForm = reactive<PreapprovalForm>({
    startDate: visitorsStore.visitDate,
    durationId: visitorsStore.entryValue || null,
    nationality,
  })

  const startDateValidator = createRequiredValidator(t('qic_empty_fields_warning.start_date'))
  const durationIdValidator = createRequiredValidator(t('qic_empty_fields_warning.duration'))

  const validation = computed(() => ({
    startDate: { startDateValidator },
    nationality: isOcrAvailable ? { required } : {},
    durationId: { durationIdValidator },
  }))

  const { v$, errors } = useValidation(validation, preapprovalForm)

  const setEntry = (id: string) => visitorsStore.setEntryValue(id)
  const formatDate = (date: Date) => setFormatDate(date, locale.value as LocaleType)

  const durations = computed(() => coreStore.durations)
  const formattedStartDate = computed(() =>
    preapprovalForm.startDate ? formatDate(preapprovalForm.startDate) : null
  )
  const formattedEndDate = computed(() => {
    const { startDate, durationId } = preapprovalForm

    if (!startDate || !durationId) return null

    const endDate = getDurationDate(durationId, startDate, durations.value)
    return formatDate(endDate)
  })

  const setDefaultDuration = () => {
    if (visitorsStore.entryValue) return
    const durationId = DURATION_KEYS.includes(coreStore.getAbValue(DURATION_CODE))
      ? durations.value[1].code
      : durations.value[0].code
    preapprovalForm.durationId = durationId
  }

  const localFindNationality = (searchString: string) =>
    findCountries(searchString, localNationalities.value)
  const onSubmit = async () => {
    v$.value.$touch()
    if (v$.value.$invalid) return

    const startDate = preapprovalForm.startDate!
    const entryId = preapprovalForm.durationId!

    visitorsStore.setVisitDate(startDate)

    if (nationality.value && isOcrAvailable) {
      visitorsStore.setHolderNationality(nationality.value)
    }

    setEntry(entryId)
    gaService.sendClickProceedByPreapprovalForm(
      'select_date',
      coreStore.getInitialReferrer,
      durations.value.find((duration) => duration.code === preapprovalForm.durationId)?.duration ||
        0,
      formatDateGlobal(startDate, 'en', 'dd/MM/yyyy')
    )
    emit('proceed')
  }

  onMounted(async () => {
    if (!preapprovalForm.startDate || preapprovalForm.startDate < minDate) {
      preapprovalForm.startDate = minDate
    }
    emit(
      'load',
      async () => {
        await coreStore.loadStoreData()
        await coreStore.getDurations()
        setDefaultDuration()
        onPerformanceCheckEnd()
      },
      'travel-date-load'
    )
  })
</script>

<template>
  <FlowCard
    :title="isOcrAvailable ? $t('visitors.travel_dates_page.title') : $t('travel_dates_page.title')"
    :title-wrap="!isOcrAvailable"
    :button-text="$t('buy_policy')"
    @click="onSubmit"
  >
    <div class="preapproval-policy">
      <!-- @vue-ignore -->
      <InputDateQC
        v-model="preapprovalForm.startDate"
        :label="$t('arrival_date_placeholder')"
        :min-date="minDate"
        :calendar-props="{ startDate: preapprovalForm.startDate ?? minDate }"
        :input-props="{
          containerAttrs: {
            'data-test': 'start-date-input',
          },
        }"
        :errors="errors.startDate"
        :locale="locale"
        data-test="start-date-input-inner"
      />

      <!-- @vue-ignore -->
      <SelectQC
        v-model="preapprovalForm.durationId"
        :options="durations"
        :label="$t('policy_duration.placeholder')"
        title-key="title"
        value-key="code"
        name="durations"
        is-filterable
        :is-clear="false"
        :locale="locale"
        :errors="errors.durationId"
        data-test="duration-input"
      />

      <CountrySelectQC
        v-if="isOcrAvailable"
        v-model="nationality"
        with-input
        :label="$t('visitors.travel_dates_page.nationality')"
        :select-props="{
          errors: errors.nationality,
          isValid: !v$.nationality.$dirty && !v$.nationality.$invalid,
          hideArrowIcon: !v$.nationality.$dirty && !v$.nationality.$invalid,
          name: 'nationality',
          filterFunction: localFindNationality,
          locale,
        }"
        :countries="localNationalities"
        is-filterable
        data-test="nationality-select"
      />

      <div v-if="formattedStartDate && formattedEndDate" class="text-medium">
        {{ $t('qlm_from') }}
        {{ formattedStartDate }}
        {{ $t('qlm_till') }}
        {{ formattedEndDate }}
      </div>
    </div>
  </FlowCard>
</template>

<style lang="scss" scoped>
  @use '@scss/utils.scss';

  .preapproval-policy {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-5);
    text-align: center;
    color: var(--colorTxtSrfPrimary);

    &__datepicker {
      margin-bottom: 16px;
      text-align: initial;
    }

    &--flow &__datepicker {
      margin-bottom: 32px;
    }

    &__chip-wrapper {
      display: flex;
      max-width: 416px;
      margin: 0 auto 24px;

      @include utils.mobile-flow {
        margin: 0 auto 16px;
      }

      .chip {
        margin-inline-end: 8px;

        &:last-child {
          margin-inline-end: 0;
        }
      }
    }

    &__chip-haya {
      max-width: 416px;
      margin: 0 auto 24px;
      white-space: pre-wrap;
    }

    &--flow &__chip-haya {
      margin: 0 auto 32px;
    }

    .chip {
      width: 100%;
      min-height: 64px;
      height: auto;
      text-align: center;
      font-size: 14px;
      line-height: 24px;
      transition: 0.2s ease-in;

      &--active {
        @include utils.chip-active();
      }

      &--disabled {
        @include utils.chip-disabled();
      }
    }

    &__title-policy {
      margin-bottom: 16px;
      font-size: 20px;
      line-height: 28px;
      font-weight: 500;
    }

    &__button {
      margin-top: 0 !important;
      font-weight: 600;
    }

    @media (max-width: 550px) {
      &--flow &__datepicker {
        margin-bottom: 24px;
      }

      &--flow &__chip-haya {
        margin: 0 auto 24px;
      }

      &__chip-wrapper .chip {
        word-spacing: 100vw;
      }

      &__button {
        font-size: 18px;
      }
    }
  }

  .policy-duration-select {
    text-align: initial;
    margin-bottom: 32px;

    @include utils.mobile-flow {
      margin-bottom: 24px;
    }
  }
</style>
